import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";

const REFRESH_MS = 10000;

const firstValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const displayValue = (value, fallback = "—") => {
  if (value === undefined || value === null || value === "") return fallback;
  if (typeof value === "boolean") return value ? "YES" : "NO";
  if (Array.isArray(value)) return value.length ? value.join("; ") : fallback;
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const scoreTone = (grade, decision) => {
  const normalizedGrade = String(grade || "").toUpperCase();
  const normalizedDecision = String(decision || "").toUpperCase();

  if (
    normalizedDecision.includes("BLOCK") ||
    normalizedDecision.includes("REJECT") ||
    ["D", "F"].includes(normalizedGrade)
  ) {
    return {
      border: "border-red-500/40",
      background: "bg-red-500/10",
      text: "text-red-300",
    };
  }

  if (
    normalizedDecision.includes("ALLOW") ||
    normalizedDecision.includes("EXECUTE") ||
    ["A", "B"].includes(normalizedGrade)
  ) {
    return {
      border: "border-emerald-500/40",
      background: "bg-emerald-500/10",
      text: "text-emerald-300",
    };
  }

  return {
    border: "border-amber-500/40",
    background: "bg-amber-500/10",
    text: "text-amber-300",
  };
};

function MetricCard({ label, value, status }) {
  const normalizedStatus = String(status || "").toUpperCase();

  const statusClass =
    normalizedStatus.includes("PASS") ||
    normalizedStatus.includes("ALLOW") ||
    normalizedStatus.includes("GOOD")
      ? "text-emerald-300"
      : normalizedStatus.includes("FAIL") ||
        normalizedStatus.includes("BLOCK") ||
        normalizedStatus.includes("HIGH")
      ? "text-red-300"
      : "text-zinc-400";

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950/80 p-4">
      <div className="text-xs font-medium uppercase tracking-wider text-zinc-500">
        {label}
      </div>

      <div className="mt-2 text-xl font-semibold text-zinc-100">
        {displayValue(value)}
      </div>

      {status ? (
        <div className={`mt-2 text-xs font-semibold ${statusClass}`}>
          {displayValue(status)}
        </div>
      ) : null}
    </div>
  );
}

export default function IdePage() {
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchContext = useCallback(async () => {
    try {
      const response = await axios.get("/api/context/decision", {
        withCredentials: true,
      });

      setContext(response.data || {});
      setError("");
      setLastUpdated(new Date());
    } catch (requestError) {
      setError(
        requestError?.response?.data?.detail ||
          requestError?.message ||
          "Unable to load IDE context."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContext();

    const interval = setInterval(fetchContext, REFRESH_MS);
    return () => clearInterval(interval);
  }, [fetchContext]);

  const view = useMemo(() => {
    const data = context || {};
    const components =
      data.components ||
      data.component_scores ||
      data.scores ||
      data.quality?.components ||
      {};

    const decision = firstValue(
      data.decision,
      data.execution_decision,
      data.action,
      data.status,
      data.allowed === true ? "ALLOW" : undefined,
      data.blocked === true ? "BLOCK" : undefined,
      "WAITING"
    );

    const grade = firstValue(
      data.grade,
      data.institutional_grade,
      data.quality?.grade,
      "—"
    );

    const score = firstValue(
      data.score,
      data.institutional_score,
      data.total_score,
      data.quality?.score,
      "—"
    );

    const reasons = firstValue(
      data.primary_reason,
      data.reason,
      data.ide_reasons,
      data.reasons,
      data.quality?.reasons,
      "No rationale available."
    );

    return {
      decision,
      grade,
      score,
      direction: firstValue(
        data.direction,
        data.trade_direction,
        data.proposed_direction,
        data.signal_direction,
        "—"
      ),
      price: firstValue(
        data.current_price,
        data.price,
        data.entry_price,
        data.market_state?.current_price,
        "—"
      ),
      permission: firstValue(
        data.permission,
        data.trade_permission,
        data.market_permission,
        data.market_state?.permission_15m,
        "—"
      ),
      location: firstValue(
        data.location,
        data.zone_label,
        data.current_zone,
        data.nearest_zone,
        data.market_location,
        "—"
      ),
      support: firstValue(
        data.nearest_support,
        data.support,
        data.support_zone,
        data.market_context?.nearest_support,
        "—"
      ),
      resistance: firstValue(
        data.nearest_resistance,
        data.resistance,
        data.resistance_zone,
        data.market_context?.nearest_resistance,
        "—"
      ),
      reasons,
      components: {
        structure: firstValue(
          components.structure,
          data.structure_score,
          data.quality?.structure_score,
          "—"
        ),
        bias: firstValue(
          components.bias_alignment,
          components.bias,
          data.bias_alignment_score,
          "—"
        ),
        location: firstValue(
          components.market_location,
          components.location,
          data.market_location_score,
          "—"
        ),
        momentum: firstValue(
          components.momentum,
          data.momentum_score,
          "—"
        ),
        riskReward: firstValue(
          components.risk_reward,
          data.risk_reward_score,
          "—"
        ),
        institutionalRisk: firstValue(
          components.institutional_risk,
          data.institutional_risk_score,
          data.risk_level,
          "—"
        ),
      },
    };
  }, [context]);

  const tone = scoreTone(view.grade, view.decision);

  return (
    <main className="min-h-[calc(100vh-57px)] bg-black px-6 py-6 text-zinc-100">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex flex-col gap-3 border-b border-zinc-800 pb-5 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.25em] text-purple-400">
              LEVELS V3
            </div>

            <h1 className="mt-2 text-3xl font-semibold tracking-tight">
              Institutional Decision Engine
            </h1>

            <p className="mt-2 max-w-3xl text-sm text-zinc-400">
              Engine A identifies the setup. The IDE grades institutional trade
              quality and determines whether execution is allowed or blocked.
            </p>
          </div>

          <div className="flex items-center gap-3 text-xs text-zinc-500">
            <span>
              {lastUpdated
                ? `Updated ${lastUpdated.toLocaleTimeString()}`
                : "Waiting for first update"}
            </span>

            <button
              type="button"
              onClick={fetchContext}
              className="rounded border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-zinc-300 hover:border-purple-500/50 hover:text-white"
            >
              Refresh
            </button>
          </div>
        </div>

        {error ? (
          <div className="mb-6 rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-red-300">
            {error}
          </div>
        ) : null}

        <section
          className={`rounded-2xl border ${tone.border} ${tone.background} p-6`}
        >
          <div className="grid gap-6 md:grid-cols-[1.3fr_0.7fr_0.7fr_0.8fr]">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
                Execution decision
              </div>

              <div className={`mt-2 text-4xl font-bold ${tone.text}`}>
                {loading ? "LOADING" : displayValue(view.decision)}
              </div>

              <div className="mt-4 text-sm leading-6 text-zinc-300">
                {displayValue(view.reasons)}
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wider text-zinc-500">
                Grade
              </div>
              <div className={`mt-2 text-5xl font-bold ${tone.text}`}>
                {displayValue(view.grade)}
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wider text-zinc-500">
                Score
              </div>
              <div className="mt-2 text-4xl font-bold text-zinc-100">
                {displayValue(view.score)}
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wider text-zinc-500">
                Direction
              </div>
              <div className="mt-2 text-3xl font-bold text-zinc-100">
                {displayValue(view.direction)}
              </div>
            </div>
          </div>
        </section>

        <section className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard label="Current price" value={view.price} />
          <MetricCard label="Trade permission" value={view.permission} />
          <MetricCard label="Market location" value={view.location} />
          <MetricCard
            label="Institutional risk"
            value={view.components.institutionalRisk}
          />
        </section>

        <section className="mt-6">
          <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-zinc-400">
            Decision scorecard
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <MetricCard
              label="Structure quality"
              value={view.components.structure}
            />
            <MetricCard
              label="Bias alignment"
              value={view.components.bias}
            />
            <MetricCard
              label="Market location"
              value={view.components.location}
            />
            <MetricCard
              label="Momentum"
              value={view.components.momentum}
            />
            <MetricCard
              label="Risk / reward"
              value={view.components.riskReward}
            />
            <MetricCard
              label="Institutional risk"
              value={view.components.institutionalRisk}
            />
          </div>
        </section>

        <section className="mt-6 grid gap-4 lg:grid-cols-2">
          <MetricCard label="Nearest major support" value={view.support} />
          <MetricCard label="Nearest major resistance" value={view.resistance} />
        </section>


          <section className="mt-8">
            <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-purple-400">
              Candidate Setups
            </div>

            <div className="space-y-4">
              {(context?.setup_candidates || []).map((candidate, index) => {
                const status = String(candidate.status || "").toUpperCase();

                const tone =
                  status === "QUALIFIED"
                    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                    : status === "BLOCK"
                    ? "border-red-500/40 bg-red-500/10 text-red-300"
                    : "border-amber-500/40 bg-amber-500/10 text-amber-300";

                return (
                  <div
                    key={`${candidate.setup_type}-${candidate.direction}-${index}`}
                    className={`rounded-xl border p-5 ${tone}`}
                  >
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">
                            #{displayValue(candidate.rank)} {displayValue(candidate.setup_type)}
                          </div>

                          {candidate.is_primary ? (
                            <span className="rounded border border-purple-500/40 bg-purple-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-purple-300">
                              Primary candidate
                            </span>
                          ) : null}
                        </div>

                        <div className="mt-2 text-2xl font-bold text-zinc-100">
                          {displayValue(candidate.direction)}
                        </div>
                      </div>

                      <div className="sm:text-right">
                        <div className="text-xs uppercase tracking-wider text-zinc-500">
                          Candidate status
                        </div>

                        <div className="mt-1 text-2xl font-bold">
                          {displayValue(candidate.lifecycle || candidate.status)}
                        </div>

                        <div className="mt-1 text-sm text-zinc-400">
                          Status {displayValue(candidate.status)}
                        </div>

                        <div className="mt-1 text-sm text-zinc-400">
                          Score {displayValue(candidate.score)}
                        </div>

                        <div className="mt-1 text-xs text-zinc-500">
                          Confirmations{" "}
                          {displayValue(candidate.confirmations_complete)}/
                          {displayValue(candidate.confirmations_required)}
                        </div>
                      </div>
                    </div>

                    <div className="mt-5 border-t border-zinc-800/80 pt-4">
                      <div className="mb-2 text-xs uppercase tracking-wider text-zinc-500">
                        Rationale
                      </div>

                      <ul className="space-y-2 text-sm text-zinc-300">
                        {(candidate.reasons || []).map((reason, reasonIndex) => (
                          <li key={reasonIndex}>• {reason}</li>
                        ))}
                      </ul>
                    </div>

                    <div className="mt-5 rounded-lg border border-zinc-800 bg-black/30 p-4">
                      <div className="flex items-center justify-between">
                        <div className="text-xs uppercase tracking-wider text-zinc-500">
                          Readiness progress
                        </div>

                        <div className="text-sm font-semibold text-zinc-200">
                          {displayValue(candidate.progress_percent, 0)}%
                        </div>
                      </div>

                      <div className="mt-3 h-2 overflow-hidden rounded-full bg-zinc-800">
                        <div
                          className="h-full rounded-full bg-purple-500 transition-all duration-300"
                          style={{
                            width: `${Math.max(
                              0,
                              Math.min(
                                100,
                                Number(candidate.progress_percent || 0)
                              )
                            )}%`,
                          }}
                        />
                      </div>

                      <div className="mt-4 space-y-2">
                        {(candidate.confirmation_matrix || []).map(
                          (item, itemIndex) => (
                            <div
                              key={itemIndex}
                              className="flex items-start gap-2 text-sm"
                            >
                              <span
                                className={
                                  item.complete
                                    ? "text-emerald-300"
                                    : "text-zinc-600"
                                }
                              >
                                {item.complete ? "✓" : "□"}
                              </span>

                              <span
                                className={
                                  item.complete
                                    ? "text-zinc-200"
                                    : "text-zinc-500"
                                }
                              >
                                {displayValue(item.name)}
                              </span>
                            </div>
                          )
                        )}
                      </div>
                    </div>

                    {(candidate.required_next_events || []).length > 0 ? (
                      <div className="mt-5 rounded-lg border border-purple-500/20 bg-black/30 p-4">
                        <div className="mb-2 text-xs uppercase tracking-wider text-purple-300">
                          Next required events
                        </div>

                        <ul className="space-y-2 text-sm text-zinc-300">
                          {candidate.required_next_events.map((event, eventIndex) => (
                            <li key={eventIndex}>□ {event}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                );
              })}

              {(context?.setup_candidates || []).length === 0 ? (
                <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-5 text-sm text-zinc-500">
                  No setup candidates are currently available.
                </div>
              ) : null}
            </div>
          </section>

        <details className="mt-6 rounded-xl border border-zinc-800 bg-zinc-950">
          <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-zinc-400">
            Raw IDE payload
          </summary>

          <pre className="max-h-96 overflow-auto border-t border-zinc-800 p-4 text-xs text-zinc-400">
            {JSON.stringify(context, null, 2)}
          </pre>
        </details>
      </div>
    </main>
  );
}
